import { Box, Autocomplete, TextField, Grid, Alert, Button, Checkbox, FormControlLabel, Dialog, DialogContent, DialogActions, Divider } from '@mui/material';
import { ArrowBackIos, Search, WarningAmber } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import useLoading from "../../hooks/useLoading";
import useStore from '../../hooks/useStore';
import DeleteIcon from '@material-ui/icons/Delete';
import useShowMessages from "../../hooks/useShowMessages";

const SettingsStoreList = ({ onClose, settingLabel, settingSelected, openSuccessModal, closeSettings, updateSetting }) => {

    const { setIsLoading } = useLoading();
    const [storeList, setStoreList] = useState([]);
    const [inputValue, setInputValue] = useState([]);
    const [showModal, setShowModal] = useState(false)
    const { unfilteredStores } = useStore();
    const [storesToUpdate, setStoresToUpdate] = useState([])
    const { showMessage } = useShowMessages();

    useEffect(() => {
        const selectedStores = unfilteredStores.filter(store => store[settingSelected.propertyName] === true);
        const mappedStores = selectedStores.map(store => ({ ...store, checked: false, value: true }));
        setStoreList(mappedStores)
    }, [unfilteredStores, settingSelected])

    const addStore = () => {
        let storesChecked = [...inputValue].map(store => ({ ...store, checked: false, value: true }))
        let mappedStores = [...storeList.concat(storesChecked)];
        const uniqueStores = mappedStores.filter((store, index) => index === mappedStores.findIndex(storeMapped => storeMapped.label === store.label));
        const sortStores = uniqueStores.sort((a, b) => parseInt(a.label.split(" - ")[0]) - parseInt(b.label.split(" - ")[0]));
        setStoresToUpdate((prevStores) => {
            storesChecked.forEach(store => {
                const storeIndex = prevStores.findIndex((prev) => store.storeId == prev.storeId)
                if(storeIndex != -1) {
                    prevStores.splice(storeIndex, 1)
                }
            })
            return [...prevStores, ...storesChecked]
        })
        setStoreList(sortStores);
        setInputValue([])
    }

    const handleStoreCheckBox = () => {
        let checkAllStores = [];
        if (storeList.filter(store => store.checked).length !== storeList.length) {
            checkAllStores = [...storeList].map(store => ({ ...store, checked: true }));
        } else {
            checkAllStores = [...storeList].map(store => ({ ...store, checked: false }));
        }
        setStoreList(checkAllStores)
    };

    const handleCheckStore = (storeIndex) => {
        let storeToUpdate = [...storeList];
        storeToUpdate[storeIndex].checked = !storeToUpdate[storeIndex].checked;
        setStoreList(storeToUpdate);
    }

    const handleSearchValue = (event, newValue) => {
        setInputValue(newValue)
    }

    const deactivateSelected = () => {
        if (storeList.length > 0) {
            const storesFiltered = storeList.filter(store => !store.checked);
            const updatedStores = storeList.map(({ checked, ...rest }) => ({ ...rest, value: checked ? false : rest.value })).filter(item => item.value === false);
            setStoresToUpdate((prevStores) => {
                updatedStores.forEach(store => {
                    const storeIndex = prevStores.findIndex((prev) => store.storeId == prev.storeId)
                    if(storeIndex !== -1) {
                        prevStores.splice(storeIndex, 1)
                    }
                })
                return [...prevStores, ...updatedStores]
            })
            setStoreList(storesFiltered)
        }
        closeModal()
    }

    const saveChanges = async () => {
        try {
            setIsLoading(true)
            const valuesToUpdate = storesToUpdate.map(({ value, label }) => ({
                value,
                storeId: label.split(' - ')[0]
            }));
            await updateSetting(valuesToUpdate)
            setIsLoading(false)
            openSuccessModal()
            closeSettings()
        } catch (error) {
            showMessage(
                "Setting couldn't be updated.",
                "error"
            );
        }
    }

    const closeModal = () => {
        setShowModal(false);
    }

    const openModal = () => {
        if (storeList.filter(store => store.checked).length > 0) {
            setShowModal(true);
        }
    }

    const checkStoreStatus = (operation) => {
        if (operation === 'checked')
            return storeList.length > 0 && storeList.filter(store => store.checked).length === storeList.length
        return storeList.length > 0 && storeList.filter(store => store.checked).length > 0 && storeList.filter(store => store.checked).length !== storeList.length
    }

    return (
        <Box sx={{ marginTop: "30px", paddingBottom: '20px' }}>
            <div className="mb-16 cursor-pointer" onClick={onClose} >
                <ArrowBackIos fontSize="small" />
                <span>Back</span>
            </div>
            <div className="title-container">{settingLabel} settings</div>
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, paddingBottom: '10px' }}>
                {
                    storeList.length === 0 && (
                        <Alert variant="filled" severity="error" sx={{ margin: '20px 20px 0 20px' }}>
                            {settingLabel} has not been activated in any stores.
                        </Alert>
                    )
                }
                <Grid container spacing={2} sx={{ padding: '25px', display: 'flex', alignItems: 'center' }}>
                    <Grid item xs={10}>
                        <Autocomplete
                            sx={{ overflow: 'auto', maxHeight: '8em', paddingY: '0.3em' }}
                            forcePopupIcon
                            limitTags={3}
                            disableCloseOnSelect
                            disableListWrap
                            fullWidth
                            size="small"
                            multiple
                            options={unfilteredStores}
                            getOptionLabel={(option) => option.label}
                            handleHomeEndKeys
                            value={inputValue}
                            onChange={handleSearchValue}
                            renderInput={(params) => (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Search sx={{ marginRight: '0.25em' }} />
                                    <TextField
                                        {...params}
                                        label="Search Stores..."
                                    />
                                </Box>
                            )}
                        />
                    </Grid>
                    <Grid item xs={2} justifyContent='flex-start'>
                        <Button className="button white small" onClick={() => addStore()}>Add</Button>
                    </Grid>
                    <Grid item container xd={12} justifyContent='space-between'>
                        <Grid item xs={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                                <Box sx={{ marginLeft: 2 }}>
                                    <FormControlLabel
                                        label="Select all"
                                        control={
                                            <Checkbox
                                                checked={checkStoreStatus('checked')}
                                                indeterminate={checkStoreStatus('indeterminate')}
                                                onChange={handleStoreCheckBox}
                                            />
                                        }
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box className='settings-stores-box'>
                        {
                            storeList.map((store, index) => (
                                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }} key={store.label}>
                                    <FormControlLabel
                                        label={store.label}
                                        control={<Checkbox checked={store.checked} onChange={() => handleCheckStore(index)} />}
                                    />
                                </Box>
                            ))
                        }
                    </Box>
                    <Grid>
                        <div className='remove-button' onClick={() => { openModal() }}>
                            Remove
                            <DeleteIcon />
                        </div>
                    </Grid>
                    <Grid item container direction='row-reverse' justifyContent='flex-start' marginBottom={'10px'}>
                        <Button className='button red small' sx={{ marginLeft: '1em !important', marginRight: '25px !important' }} onClick={saveChanges}>Save</Button>
                        <Button className='button white small' onClick={onClose}>Cancel</Button>
                    </Grid>
                </Grid>
            </Box>

            {
                showModal && (
                    <Dialog
                        sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435, backgroundColor: '#fdf0e5', color: '#5f2b01', fontWeight: 400 } }}
                        maxWidth="xs"
                        open={showModal}
                    >
                        <DialogContent sx={{ display: 'flex', alignItems: 'center' }}>
                            <WarningAmber fontSize='large' sx={{ mr: 2 }} />
                            Are you sure you want to deactivate {settingLabel} for the selected stores?
                        </DialogContent>
                        <Divider />
                        <DialogActions>
                            <Button autoFocus onClick={deactivateSelected} sx={{ color: '#5f2b01' }}>
                                Yes
                            </Button>
                            <Button onClick={closeModal} sx={{ color: '#5f2b01' }}>No</Button>
                        </DialogActions>
                    </Dialog>
                )
            }
        </Box>
    );
}

export default SettingsStoreList;